/**
 * Global search (docs/11 F-6; migration 0051).
 *
 * ONE box, every kind of record, ranked. The catalog below is the authority for
 * which kinds are searchable — the DB builders (`app_search_doc_<kind>`), the
 * result icons and the filter chips all read it, so a kind cannot exist in one
 * of those places and not the others.
 */

import { z } from 'zod';

/**
 * Every searchable kind, in the order results are grouped and ties are broken.
 *
 * The order is a judgement about what people are usually looking for, and it is
 * v1's own: its search screen led with properties, and Matt's team navigates by
 * building. A tenancy ("Meridian Legal at Ballantyne") outranks the company
 * record because the question is nearly always "who is in this suite", not
 * "tell me about this company".
 *
 * `href` is a template, not a route match — `:id` is the only substitution, and
 * gate 9 checks each one against the route manifest.
 */
export const SEARCH_KINDS = [
  { key: 'property', label: 'Property', plural: 'Properties' },
  { key: 'tenancy', label: 'Tenancy', plural: 'Tenancies' },
  { key: 'vendor', label: 'Vendor', plural: 'Vendors' },
  { key: 'tenant_org', label: 'Tenant', plural: 'Tenants' },
  { key: 'agreement', label: 'Agreement', plural: 'Agreements' },
  { key: 'lease', label: 'Lease', plural: 'Leases' },
  { key: 'space', label: 'Suite', plural: 'Suites' },
  { key: 'property_group', label: 'Property group', plural: 'Property groups' },
  { key: 'utility_account', label: 'Utility account', plural: 'Utility accounts' },
  { key: 'management_agreement', label: 'Management agreement', plural: 'Management agreements' },
  { key: 'document', label: 'Document', plural: 'Documents' },
  { key: 'vendor_contact', label: 'Vendor contact', plural: 'Vendor contacts' },
  { key: 'tenant_contact', label: 'Tenant contact', plural: 'Tenant contacts' },
  { key: 'internal_contact', label: 'TKC contact', plural: 'TKC contacts' },
  /**
   * The FIFTEENTH kind (0057). Matt's directive 2: a completed action item must
   * go "somewhere for historical reference (and still search able when made
   * inactive - i.e. inactive = completed)" — which the index could not keep as a
   * promise while `key_date` was not a kind at all.
   *
   * Labelled ACTION ITEM, not "key date": ADR-50 settled that the one word for
   * dated work is what a reader sees, and the record noun stays `key_date` in
   * the schema. The builder maps open → active and done/cancelled → inactive, so
   * search's inactive toggle IS the completed archive.
   */
  { key: 'key_date', label: 'Action item', plural: 'Action items' },
] as const;

export type SearchKind = (typeof SEARCH_KINDS)[number]['key'];

export const SEARCH_KIND_KEYS = SEARCH_KINDS.map((kind) => kind.key) as readonly SearchKind[];

/** Rank of a kind in the display order above; used to break score ties. */
export const searchKindOrder = (kind: string): number => {
  const found = SEARCH_KINDS.findIndex((entry) => entry.key === kind);
  return found === -1 ? SEARCH_KINDS.length : found;
};

export const searchKindLabel = (kind: string): string =>
  SEARCH_KINDS.find((entry) => entry.key === kind)?.label ?? kind;

/**
 * Where a hit goes when you click it — ONE function, so no screen invents a URL.
 *
 * Three of the eight kinds have no route of their own, and pretending otherwise
 * was the first thing this file got wrong: a vendor contact and a tenant contact
 * are reached through their PARENT's Contacts tab, because that is where they
 * are edited and where the rest of the vendor's people are. Only the internal
 * directory has per-contact pages. So the destination needs the hit, not just
 * an id — which is also the honest shape, since a search result that cannot say
 * where it goes is not a result.
 *
 * A contact whose parent id is missing falls back to its parent LIST rather
 * than to a dead link. That should not happen (the index carries the parent id
 * for exactly this reason) and the fallback exists so a stale row is a mildly
 * wrong destination instead of a 404.
 */
export function searchHref(hit: {
  readonly kind: SearchKind;
  readonly id: string;
  readonly propertyId?: string | null;
  readonly vendorId?: string | null;
  readonly tenantOrgId?: string | null;
  readonly tenancyId?: string | null;
}): string {
  switch (hit.kind) {
    case 'property':
      return `/properties/${hit.id}`;
    case 'tenancy':
      return `/tenancies/${hit.id}`;
    case 'vendor':
      return `/vendors/${hit.id}`;
    case 'tenant_org':
      return `/tenants/${hit.id}`;
    case 'property_group':
      return `/property-groups/${hit.id}`;
    case 'internal_contact':
      return `/contacts/${hit.id}`;
    case 'agreement':
      return `/agreements/${hit.id}`;
    case 'document':
      return `/documents/${hit.id}`;
    case 'management_agreement':
      return `/management-agreements/${hit.id}`;
    // A LEASE and a SUITE have no page of their own: both are reached through
    // the record they belong to, which is also where they are edited. Sending
    // somebody to a tab that exists beats inventing a route that does not.
    case 'lease':
      return hit.tenancyId === null || hit.tenancyId === undefined
        ? '/tenants'
        : `/tenancies/${hit.tenancyId}`;
    case 'space':
      // Suites live on the property's own screen rather than a route of their
      // own, so a suite hit lands on the building it is in.
      return hit.propertyId === null || hit.propertyId === undefined
        ? '/properties'
        : `/properties/${hit.propertyId}`;
    case 'utility_account':
      return `/utility-accounts/${hit.id}`;
    // An action item HAS a page of its own (0045), which is where its notes,
    // its owners and its completion note already live — so a completed one found
    // in search opens the record rather than a filtered list it sits inside.
    case 'key_date':
      return `/key-dates/${hit.id}`;
    case 'vendor_contact':
      return hit.vendorId === null || hit.vendorId === undefined
        ? '/vendors'
        : `/vendors/${hit.vendorId}/contacts`;
    case 'tenant_contact':
      // The TENANCY tab when the contact belongs to one location, the company
      // record when it is company-wide — the same distinction the contact form
      // makes with its "Only for <property>" checkbox.
      if (hit.tenancyId !== null && hit.tenancyId !== undefined)
        return `/tenancies/${hit.tenancyId}/contacts`;
      return hit.tenantOrgId === null || hit.tenantOrgId === undefined
        ? '/tenants'
        : `/tenants/${hit.tenantOrgId}`;
  }
}

/**
 * `q` is NOT trimmed to a minimum length here. One character is a legitimate
 * search — "4" finds 4064 Colony Rd — and the server answers an empty query
 * with an empty result rather than an error, because a search box that errors
 * while you are still typing is the search box people stop using.
 */
export const searchQuerySchema = z.object({
  q: z.string().max(200).default(''),
  kind: z.enum(SEARCH_KIND_KEYS as [SearchKind, ...SearchKind[]]).optional(),
  /**
   * Default `active`. v1's toggle was exclusive — turning "show inactive" on
   * hid every ACTIVE record, which reads as a bug the first time you use it —
   * so this one is a three-way: active (default), inactive, or all.
   */
  status: z.enum(['active', 'inactive', 'all']).default('active'),
  limit: z.coerce.number().int().min(1).max(50).default(20),
});

export type SearchQuery = z.infer<typeof searchQuerySchema>;

export interface SearchHit {
  readonly kind: SearchKind;
  readonly id: string;
  readonly title: string;
  readonly subtitle: string | null;
  readonly status: 'active' | 'inactive';
  /** Computed server-side with `searchHref`, so the link is decided once. */
  readonly href: string;
  /** The property this hangs off, when it has one — for the result's context. */
  readonly propertyId: string | null;
  readonly vendorId: string | null;
  readonly tenantOrgId: string | null;
  readonly tenancyId: string | null;
  /** True when the caller has favorited this record. */
  readonly favorite: boolean;
}

export interface SearchResponse {
  /** Echoed back so a late response can be discarded against the current box. */
  readonly query: string;
  readonly hits: readonly SearchHit[];
  /** Per-kind totals BEFORE the limit, so the chips can show real counts. */
  readonly counts: Readonly<Record<string, number>>;
  readonly total: number;
  /**
   * True when the ranked list was cut at `limit`. The UI says so rather than
   * ending as though nothing came after — the same rule the history tab's cap
   * follows (docs/01 §7).
   */
  readonly truncated: boolean;
  /**
   * True when nothing matched the words and these are SPELLING-tolerant
   * results instead. The UI must say "showing close matches", because silently
   * returning something else for what you typed is worse than an empty state.
   */
  readonly fuzzy: boolean;
}
